import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, User, Lock, Settings as SettingsIcon, Monitor, Smartphone, Tablet, LogOut, Loader2 } from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/ui/Card';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import toast from 'react-hot-toast';
import { authService } from '../../services';
import api from '../../services/api';
import { useTheme } from '../../contexts/ThemeContext';
import { usePreferences } from '../../contexts/PreferencesContext';

/**
 * Settings Page
 * Universal settings page for all user roles (Admin, Client, Trainer, Participant)
 */

const SettingsPage = () => {
  const navigate = useNavigate();
  const { setTheme } = useTheme();
  const {
    setLanguage: setContextLanguage,
    setTimezone: setContextTimezone,
  } = usePreferences();
  const [activeTab, setActiveTab] = useState('profile'); // profile, security, preferences
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const fileInputRef = useRef(null);

  // Get user from localStorage
  const [user, setUser] = useState(() => {
    const userData = localStorage.getItem('user');
    return userData ? JSON.parse(userData) : {
      firstName: 'John',
      lastName: 'Doe',
      email: 'john@example.com',
      phone: '+1-555-0123',
      role: 'client',
      organization: 'ABC Corp',
      jobTitle: 'L&D Manager',
      avatar: null,
    };
  });

  // Profile settings state
  const [profileData, setProfileData] = useState({
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    email: user.email || '',
    phone: user.phone || '',
    jobTitle: user.jobTitle || '',
    bio: user.bio || '',
  });

  // Security settings state
  const [securityData, setSecurityData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    twoFactorEnabled: false,
  });

  // Notification settings state
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    sessionReminders: true,
    activityAlerts: true,
    weeklyReports: true,
    marketingEmails: false,
    pushNotifications: true,
  });

  // Preferences state
  const [preferences, setPreferences] = useState({
    language: 'en',
    timezone: 'UTC',
    theme: 'light',
  });

  // Fetch settings from API on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setIsLoading(true);
        const response = await authService.getSettings();
        if (response.success) {
          // Update notification settings
          setNotificationSettings(response.data.notifications);
          // Update preferences
          setPreferences(response.data.preferences);
          // Update security settings
          setSecurityData(prev => ({
            ...prev,
            twoFactorEnabled: response.data.security.twoFactorEnabled,
          }));
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
    // Load avatar from user data
    if (user?.avatar) {
      const apiBase = (process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:5000/api' : '/api')).replace(/\/api\/?$/, '');
      const stored = user.avatar;
      const url = stored.startsWith('http') || stored.startsWith('/')
        ? (stored.startsWith('http') ? stored : `${apiBase}${stored}`)
        : `${apiBase}/uploads/profiles/${stored}`;
      setAvatarUrl(url);
    }
  }, []);

  const handleProfileChange = (e) => {
    setProfileData({
      ...profileData,
      [e.target.name]: e.target.value,
    });
  };

  const [passwordErrors, setPasswordErrors] = useState({});

  const handleSecurityChange = (e) => {
    const { name, value } = e.target;
    const updated = { ...securityData, [name]: value };
    setSecurityData(updated);

    // Real-time password validation
    if (name === 'newPassword') {
      if (value.length > 0 && value.length < 8) {
        setPasswordErrors((prev) => ({ ...prev, newPassword: 'Password must be at least 8 characters' }));
      } else {
        setPasswordErrors((prev) => ({ ...prev, newPassword: '' }));
      }
      if (updated.confirmPassword && value !== updated.confirmPassword) {
        setPasswordErrors((prev) => ({ ...prev, confirmPassword: 'Passwords do not match' }));
      } else if (updated.confirmPassword) {
        setPasswordErrors((prev) => ({ ...prev, confirmPassword: '' }));
      }
    } else if (name === 'confirmPassword') {
      if (value && value !== updated.newPassword) {
        setPasswordErrors((prev) => ({ ...prev, confirmPassword: 'Passwords do not match' }));
      } else {
        setPasswordErrors((prev) => ({ ...prev, confirmPassword: '' }));
      }
    }
  };

  const handleNotificationToggle = (key) => {
    setNotificationSettings({
      ...notificationSettings,
      [key]: !notificationSettings[key],
    });
  };

  const handlePreferenceChange = (e) => {
    const { name, value } = e.target;
    setPreferences({
      ...preferences,
      [name]: value,
    });

    // Immediately apply preference changes
    if (name === 'theme') {
      setTheme(value);
    } else if (name === 'language') {
      setContextLanguage(value);
    } else if (name === 'timezone') {
      setContextTimezone(value);
    }
  };

  // Handle avatar photo upload
  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!['image/jpeg', 'image/png', 'image/gif'].includes(file.type)) {
      toast.error('Please upload a JPG, PNG, or GIF image');
      return;
    }

    // Validate file size (2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('image', file);
      const response = await api.upload('/upload/profile', formData);
      if (response.success || response.data) {
        const apiBase = (process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:5000/api' : '/api')).replace(/\/api\/?$/, '');
        const relativeUrl = response.data?.url || response.url;
        const filename = response.data?.filename || response.filename;
        const displayUrl = relativeUrl ? `${apiBase}${relativeUrl}` : null;
        setAvatarUrl(displayUrl);
        // Update user in localStorage
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        storedUser.avatar = filename;
        localStorage.setItem('user', JSON.stringify(storedUser));
        window.dispatchEvent(new Event('user-updated'));
        toast.success('Profile photo updated!');
      }
    } catch (error) {
      toast.error('Failed to upload photo');
      console.error('Photo upload error:', error);
    }
    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const [showRemovePhotoConfirm, setShowRemovePhotoConfirm] = useState(false);

  // Active sign-in sessions (one per device that's currently logged in with
  // this account). Backed by AuthSession on the server — populated when the
  // Security tab opens.
  const [authSessions, setAuthSessions] = useState([]);
  const [authSessionsLoading, setAuthSessionsLoading] = useState(false);
  const [revokingId, setRevokingId] = useState(null);
  const [revokingOthers, setRevokingOthers] = useState(false);

  const fetchAuthSessions = async () => {
    setAuthSessionsLoading(true);
    try {
      const res = await api.get('/auth/sessions');
      if (res?.success) setAuthSessions(res.data || []);
    } catch (err) {
      console.error('[Settings] Failed to load active sessions:', err);
    } finally {
      setAuthSessionsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'security') fetchAuthSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleRevokeSession = async (id) => {
    setRevokingId(id);
    try {
      const res = await api.delete(`/auth/sessions/${id}`);
      if (res?.success) {
        toast.success('Signed out from that device');
        setAuthSessions((prev) => prev.filter((s) => s.id !== id));
      } else {
        toast.error(res?.error || 'Failed to sign out');
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to sign out');
    } finally {
      setRevokingId(null);
    }
  };

  const handleRevokeOthers = async () => {
    setRevokingOthers(true);
    try {
      const res = await api.delete('/auth/sessions/others');
      if (res?.success) {
        toast.success(`Signed out from ${res.data?.revoked || 0} other device(s)`);
        setAuthSessions((prev) => prev.filter((s) => s.current));
      } else {
        toast.error(res?.error || 'Failed to sign out other devices');
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to sign out other devices');
    } finally {
      setRevokingOthers(false);
    }
  };

  const formatRelative = (d) => {
    if (!d) return '';
    const ms = Date.now() - new Date(d).getTime();
    if (Number.isNaN(ms)) return '';
    const min = Math.floor(ms / 60000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min} min ago`;
    const hr = Math.floor(ms / 3600000);
    if (hr < 24) return `${hr} hr ago`;
    const d2 = Math.floor(ms / 86400000);
    if (d2 < 7) return `${d2} day${d2 === 1 ? '' : 's'} ago`;
    return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const deviceIcon = (deviceType) => {
    if (deviceType === 'mobile') return Smartphone;
    if (deviceType === 'tablet') return Tablet;
    return Monitor;
  };

  const handleRemovePhoto = async () => {
    try {
      await api.delete('/upload/profile');
      setAvatarUrl(null);
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      storedUser.avatar = null;
      localStorage.setItem('user', JSON.stringify(storedUser));
      window.dispatchEvent(new Event('user-updated'));
      toast.success('Profile photo removed');
    } catch (error) {
      toast.error('Failed to remove photo');
      console.error('Photo remove error:', error);
    }
  };

  const handleSaveProfile = async () => {
    setIsSaving(true);
    try {
      const response = await authService.updateProfile(profileData);

      if (response.success) {
        // Update local user state with response data
        setUser(response.data);
        toast.success('Profile updated successfully!');
      } else {
        throw new Error(response.error || 'Failed to update profile');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!securityData.currentPassword || !securityData.newPassword) {
      toast.error('Please fill in all password fields');
      return;
    }

    if (securityData.newPassword !== securityData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (securityData.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setIsSaving(true);
    try {
      const response = await authService.updatePassword(
        securityData.currentPassword,
        securityData.newPassword
      );

      if (response.success) {
        toast.success('Password changed successfully!');
        setSecurityData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
          twoFactorEnabled: securityData.twoFactorEnabled,
        });
      } else {
        throw new Error(response.error || 'Failed to change password');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to change password');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveNotifications = async () => {
    setIsSaving(true);
    try {
      const response = await authService.updateNotificationSettings(notificationSettings);
      if (response.success) {
        toast.success(response.message || 'Notification settings saved!');
      } else {
        throw new Error(response.error || 'Failed to save settings');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSavePreferences = async () => {
    setIsSaving(true);
    try {
      const response = await authService.updatePreferences(preferences);
      if (response.success) {
        toast.success(response.message || 'Preferences saved!');
      } else {
        throw new Error(response.error || 'Failed to save preferences');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to save preferences');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDownloadData = () => {
    try {
      // Compile user data for download
      const userData = {
        profile: {
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          phone: user.phone,
          jobTitle: user.jobTitle,
          bio: user.bio,
          role: user.role,
          organization: typeof user.organization === 'object' ? user.organization?.name : user.organization,
        },
        preferences: preferences,
        notificationSettings: notificationSettings,
        exportedAt: new Date().toISOString(),
      };

      // Create and download JSON file
      const dataStr = JSON.stringify(userData, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `my-data-${user.firstName?.toLowerCase() || 'user'}-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('Your data has been downloaded!');
    } catch (error) {
      toast.error('Failed to download data');
      console.error('Download error:', error);
    }
  };

  // TODO: Implement real 2FA with TOTP/authenticator app in the future
  // handleToggleTwoFactor removed — was a stub with no actual TOTP verification

  const tabs = [
    { id: 'profile',     label: 'Profile',     Icon: User },
    { id: 'security',    label: 'Security',    Icon: Lock },
    // { id: 'notifications', label: 'Notifications', Icon: Bell }, // Hidden until notification system is implemented
    // { id: 'preferences', label: 'Preferences', Icon: SettingsIcon }, // Hidden — language/timezone/theme not wired through the app yet
  ];

  // Navigate back based on user role
  const handleBack = () => {
    const userRole = user.role?.toLowerCase() || 'client';
    if (userRole === 'super-admin') {
      navigate('/admin/dashboard');
    } else if (userRole === 'client') {
      navigate('/client/dashboard');
    } else if (userRole === 'trainer') {
      navigate('/trainer/dashboard');
    } else {
      navigate(-1);
    }
  };

  const handleCancel = () => {
    // Navigate back to dashboard based on user role
    handleBack();
  };

  return (
    <DashboardLayout>
      <div className="w-full px-4 lg:px-6 py-6 space-y-4">
        {/* Back Button */}
        <button
          type="button"
          onClick={handleBack}
          className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100 transition-colors group"
        >
          <ArrowLeft size={14} className="transition-transform group-hover:-translate-x-0.5" />
          <span className="font-medium">Back to Dashboard</span>
        </button>

        {/* Header */}
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-50">Settings</h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">Manage your account settings and preferences</p>
        </div>

        {/* Tabs — segmented pill bar with icons */}
        <div className="sticky top-0 z-10 -mx-4 px-4 lg:-mx-6 lg:px-6 py-2 bg-neutral-50/70 dark:bg-neutral-950/60 backdrop-blur-md border-b border-neutral-200/70 dark:border-neutral-800/70">
          <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-white dark:bg-neutral-900 ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-800 shadow-[0_1px_2px_rgba(16,24,40,0.04)] overflow-x-auto scrollbar-hide">
            {tabs.map(({ id, label, Icon: TabIcon }) => {
              const active = activeTab === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setActiveTab(id)}
                  className={`px-3.5 py-2 rounded-lg text-[15px] font-medium transition-colors inline-flex items-center gap-1.5 whitespace-nowrap flex-shrink-0 ${
                    active
                      ? 'bg-primary-50 text-primary-700 ring-1 ring-inset ring-primary-200 dark:bg-primary-500/10 dark:text-primary-300 dark:ring-primary-500/30'
                      : 'text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100'
                  }`}
                >
                  <TabIcon size={16} strokeWidth={1.75} />
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <Card title="Profile Information" padding="lg">
              <div className="space-y-6">
                {/* Avatar Section */}
                <div className="flex items-center gap-4 sm:gap-6">
                  <div className="w-16 h-16 sm:w-24 sm:h-24 bg-primary-100 rounded-full flex items-center justify-center overflow-hidden">
                    {avatarUrl ? (
                      <img src={avatarUrl} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-2xl sm:text-4xl font-bold text-primary-600">
                        {profileData.firstName?.[0]}{profileData.lastName?.[0]}
                      </span>
                    )}
                  </div>
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/gif"
                      onChange={handlePhotoUpload}
                      className="hidden"
                    />
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button variant="secondary" size="sm" onClick={() => fileInputRef.current?.click()}>
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        Change Photo
                      </Button>
                      {avatarUrl && (
                        <Button variant="ghost" size="sm" onClick={() => setShowRemovePhotoConfirm(true)}>
                          Remove
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-neutral-600 mt-2">JPG, PNG or GIF. Max size 2MB</p>
                  </div>
                </div>

                {/* Profile Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  <Input
                    label="First Name"
                    name="firstName"
                    value={profileData.firstName}
                    onChange={handleProfileChange}
                    required
                    fullWidth
                  />
                  <Input
                    label="Last Name"
                    name="lastName"
                    value={profileData.lastName}
                    onChange={handleProfileChange}
                    required
                    fullWidth
                  />
                  <Input
                    label="Email"
                    name="email"
                    type="email"
                    value={profileData.email}
                    onChange={handleProfileChange}
                    required
                    fullWidth
                  />
                  <Input
                    label="Phone"
                    name="phone"
                    type="tel"
                    value={profileData.phone}
                    onChange={handleProfileChange}
                    fullWidth
                  />
                  <Input
                    label="Job Title"
                    name="jobTitle"
                    value={profileData.jobTitle}
                    onChange={handleProfileChange}
                    fullWidth
                  />
                  {/* Only show Organization for non-super-admin users */}
                  {user.role !== 'super-admin' && (
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                        Organization
                      </label>
                      <p className="px-4 py-2.5 bg-neutral-100 dark:bg-neutral-700 border border-neutral-200 dark:border-neutral-600 rounded-lg text-neutral-600 dark:text-neutral-300">
                        {typeof user.organization === 'object' ? user.organization?.name : user.organization}
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Bio
                  </label>
                  <textarea
                    name="bio"
                    value={profileData.bio}
                    onChange={handleProfileChange}
                    className="w-full px-4 py-2.5 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100"
                    rows="4"
                    placeholder="Tell us about yourself..."
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-neutral-200 dark:border-neutral-700">
                  <Button variant="ghost" onClick={handleCancel}>Cancel</Button>
                  <Button variant="primary" onClick={handleSaveProfile} disabled={isSaving}>
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <Card title="Change Password" padding="lg">
              <div className="space-y-6">
                <div>
                  <Input
                    label="Current Password"
                    name="currentPassword"
                    type="password"
                    value={securityData.currentPassword}
                    onChange={handleSecurityChange}
                    required
                    fullWidth
                  />
                  <Link
                    to="/forgot-password"
                    state={{ email: profileData.email }}
                    className="inline-block mt-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div>
                  <Input
                    label="New Password"
                    name="newPassword"
                    type="password"
                    value={securityData.newPassword}
                    onChange={handleSecurityChange}
                    required
                    fullWidth
                  />
                  {passwordErrors.newPassword ? (
                    <p className="text-sm text-red-600 mt-1">{passwordErrors.newPassword}</p>
                  ) : (
                    <p className="text-xs text-neutral-500 mt-1">Must be at least 8 characters</p>
                  )}
                </div>
                <div>
                  <Input
                    label="Confirm New Password"
                    name="confirmPassword"
                    type="password"
                    value={securityData.confirmPassword}
                    onChange={handleSecurityChange}
                    required
                    fullWidth
                  />
                  {passwordErrors.confirmPassword && (
                    <p className="text-sm text-red-600 mt-1">{passwordErrors.confirmPassword}</p>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-neutral-200 dark:border-neutral-700">
                  <Button variant="ghost" onClick={handleCancel}>Cancel</Button>
                  <Button variant="primary" onClick={handleChangePassword} disabled={isSaving}>
                    {isSaving ? 'Updating...' : 'Update Password'}
                  </Button>
                </div>
              </div>
            </Card>

            <Card title="Active Sessions" padding="lg">
              <div className="flex items-start justify-between gap-3 mb-4">
                <p className="text-xs sm:text-sm text-neutral-600 dark:text-neutral-400">
                  Devices currently signed in with this account. Sign out a device remotely if you don't recognize it.
                </p>
                {authSessions.filter((s) => !s.current).length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleRevokeOthers}
                    disabled={revokingOthers}
                  >
                    {revokingOthers ? (
                      <><Loader2 size={14} className="animate-spin mr-1.5" /> Signing out…</>
                    ) : (
                      <><LogOut size={14} className="mr-1.5" /> Sign out other devices</>
                    )}
                  </Button>
                )}
              </div>

              {authSessionsLoading && authSessions.length === 0 ? (
                <div className="flex items-center justify-center py-8 text-neutral-500">
                  <Loader2 size={16} className="animate-spin mr-2" />
                  <span className="text-sm">Loading sessions…</span>
                </div>
              ) : authSessions.length === 0 ? (
                <div className="text-center py-6 text-sm text-neutral-500 dark:text-neutral-400">
                  No active sessions found.
                </div>
              ) : (
                <div className="space-y-3">
                  {authSessions.map((s) => {
                    const Icon = deviceIcon(s.deviceType);
                    return (
                      <div
                        key={s.id}
                        className="flex items-center justify-between gap-3 p-3 sm:p-4 bg-neutral-50 dark:bg-neutral-700/40 rounded-lg ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-700"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 flex-shrink-0">
                            <Icon size={18} strokeWidth={1.75} />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">
                                {s.label || `${s.browser} on ${s.os}`}
                              </p>
                              {s.current && <Badge variant="success" size="sm">This device</Badge>}
                            </div>
                            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                              {s.current
                                ? 'Active now'
                                : `Last active ${formatRelative(s.lastActiveAt)}`}
                              {s.ip ? ` · ${s.ip}` : ''}
                            </p>
                          </div>
                        </div>

                        {!s.current && (
                          <button
                            type="button"
                            onClick={() => handleRevokeSession(s.id)}
                            disabled={revokingId === s.id}
                            className="text-xs font-medium text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-500/10 px-3 py-1.5 rounded-lg ring-1 ring-inset ring-rose-200/70 dark:ring-rose-500/30 transition-colors disabled:opacity-50 inline-flex items-center gap-1.5"
                          >
                            {revokingId === s.id ? (
                              <><Loader2 size={12} className="animate-spin" /> Signing out…</>
                            ) : (
                              <><LogOut size={12} /> Sign out</>
                            )}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          </motion.div>
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <Card title="Notifications" padding="lg">
              <div className="space-y-6">
                {Object.entries({
                  sessionReminders: 'Session Reminders',
                  activityAlerts: 'Activity Start Alerts',
                  weeklyReports: 'Weekly Performance Reports',
                }).map(([key, label]) => (
                  <div key={key} className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-xs sm:text-sm font-medium text-neutral-900 dark:text-neutral-100">{label}</p>
                      <p className="text-[10px] sm:text-sm text-neutral-600 dark:text-neutral-400 mt-1">
                        {key === 'sessionReminders' && 'Get notified 30 minutes before your session starts'}
                        {key === 'activityAlerts' && 'Receive instant notifications when activities begin'}
                        {key === 'weeklyReports' && 'Summary of your weekly performance and progress'}
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notificationSettings[key]}
                        onChange={() => handleNotificationToggle(key)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-neutral-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>
                ))}

                <div className="flex justify-end gap-3 pt-4 border-t border-neutral-200 dark:border-neutral-700">
                  <Button variant="ghost" onClick={handleBack}>Cancel</Button>
                  <Button variant="primary" onClick={handleSaveNotifications} disabled={isSaving}>
                    {isSaving ? 'Saving...' : 'Save Preferences'}
                  </Button>
                </div>
              </div>
            </Card>

            <Card title="Push Notifications" padding="lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm font-medium text-neutral-900 dark:text-neutral-100">Browser Push Notifications</p>
                  <p className="text-[10px] sm:text-sm text-neutral-600 dark:text-neutral-400 mt-1">
                    Receive real-time notifications in your browser
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notificationSettings.pushNotifications}
                    onChange={() => handleNotificationToggle('pushNotifications')}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-neutral-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Preferences Tab — hidden for now. Language/timezone/theme aren't
            wired through the app yet (UI is fully built, but selecting a
            timezone doesn't change session times, etc.). Kept in code so we
            can flip it back on once the wiring lands. */}
        {false && activeTab === 'preferences' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <Card title="General Preferences" padding="lg">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Language
                  </label>
                  <select
                    name="language"
                    value={preferences.language}
                    onChange={handlePreferenceChange}
                    className="w-full px-4 py-2.5 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100"
                  >
                    <option value="en">English</option>
                    <option value="es">Spanish</option>
                    <option value="fr">French</option>
                    <option value="de">German</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Timezone
                  </label>
                  <select
                    name="timezone"
                    value={preferences.timezone}
                    onChange={handlePreferenceChange}
                    className="w-full px-4 py-2.5 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100"
                  >
                    <optgroup label="Americas">
                      <option value="America/New_York">Eastern Time (ET)</option>
                      <option value="America/Chicago">Central Time (CT)</option>
                      <option value="America/Denver">Mountain Time (MT)</option>
                      <option value="America/Los_Angeles">Pacific Time (PT)</option>
                      <option value="America/Anchorage">Alaska Time (AKT)</option>
                      <option value="Pacific/Honolulu">Hawaii Time (HT)</option>
                      <option value="America/Toronto">Toronto (ET)</option>
                      <option value="America/Mexico_City">Mexico City (CST)</option>
                      <option value="America/Sao_Paulo">São Paulo (BRT)</option>
                      <option value="America/Buenos_Aires">Buenos Aires (ART)</option>
                    </optgroup>
                    <optgroup label="Europe">
                      <option value="UTC">UTC (GMT+0)</option>
                      <option value="Europe/London">London (GMT)</option>
                      <option value="Europe/Paris">Paris (CET)</option>
                      <option value="Europe/Berlin">Berlin (CET)</option>
                      <option value="Europe/Amsterdam">Amsterdam (CET)</option>
                      <option value="Europe/Madrid">Madrid (CET)</option>
                      <option value="Europe/Rome">Rome (CET)</option>
                      <option value="Europe/Moscow">Moscow (MSK)</option>
                      <option value="Europe/Istanbul">Istanbul (TRT)</option>
                    </optgroup>
                    <optgroup label="Asia & Pacific">
                      <option value="Asia/Dubai">Dubai (GST)</option>
                      <option value="Asia/Karachi">Karachi (PKT)</option>
                      <option value="Asia/Kolkata">India (IST)</option>
                      <option value="Asia/Dhaka">Dhaka (BST)</option>
                      <option value="Asia/Bangkok">Bangkok (ICT)</option>
                      <option value="Asia/Singapore">Singapore (SGT)</option>
                      <option value="Asia/Hong_Kong">Hong Kong (HKT)</option>
                      <option value="Asia/Shanghai">Shanghai (CST)</option>
                      <option value="Asia/Tokyo">Tokyo (JST)</option>
                      <option value="Asia/Seoul">Seoul (KST)</option>
                      <option value="Australia/Sydney">Sydney (AEST)</option>
                      <option value="Australia/Melbourne">Melbourne (AEST)</option>
                      <option value="Pacific/Auckland">Auckland (NZST)</option>
                    </optgroup>
                    <optgroup label="Africa & Middle East">
                      <option value="Africa/Cairo">Cairo (EET)</option>
                      <option value="Africa/Johannesburg">Johannesburg (SAST)</option>
                      <option value="Africa/Lagos">Lagos (WAT)</option>
                      <option value="Asia/Jerusalem">Jerusalem (IST)</option>
                      <option value="Asia/Riyadh">Riyadh (AST)</option>
                    </optgroup>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Theme
                  </label>
                  <select
                    name="theme"
                    value={preferences.theme}
                    onChange={handlePreferenceChange}
                    className="w-full px-4 py-2.5 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100"
                  >
                    <option value="light">Light</option>
                    <option value="dark">Dark</option>
                    <option value="auto">Auto (System)</option>
                  </select>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-neutral-200 dark:border-neutral-700">
                  <Button variant="ghost" onClick={handleBack}>Cancel</Button>
                  <Button variant="primary" onClick={handleSavePreferences} disabled={isSaving}>
                    {isSaving ? 'Saving...' : 'Save Preferences'}
                  </Button>
                </div>
              </div>
            </Card>

            <Card title="Data & Privacy" padding="lg">
              <div className="space-y-4">
                <Button variant="ghost" fullWidth onClick={handleDownloadData}>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download My Data
                </Button>
              </div>
            </Card>
          </motion.div>
        )}
      </div>

      <ConfirmDialog
        isOpen={showRemovePhotoConfirm}
        onClose={() => setShowRemovePhotoConfirm(false)}
        onConfirm={handleRemovePhoto}
        title="Remove Profile Photo?"
        message="Your photo will be deleted and replaced with your initials. You can upload a new one anytime."
        confirmText="Remove"
        cancelText="Cancel"
        variant="danger"
      />
    </DashboardLayout>
  );
};

export default SettingsPage;
